import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Success = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { clearCart } = useCart();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orderData, setOrderData] = useState(null);
    
    // Get session_id from URL (Stripe redirect) or from location state (legacy PayPal)
    const sessionId = searchParams.get('session_id');
    const legacyState = location.state;

    useEffect(() => {
        const pollPaymentStatus = async (attempts = 0) => {
            const maxAttempts = 10;
            const pollInterval = 2000; // 2 seconds

            if (attempts >= maxAttempts) {
                setError('Payment verification timed out. Please check your email for confirmation or contact support.');
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
                const data = response.data;

                if (data.status === 'completed' || data.payment_status === 'paid') {
                    setOrderData({
                        downloadLinks: data.download_links || [],
                        email: data.email || '',
                        orderId: data.order_id || ''
                    });
                    clearCart(); // Clear cart after successful payment
                    setLoading(false);
                    return;
                } else if (data.status === 'expired') {
                    setError('Payment session expired. Please try again.');
                    setLoading(false);
                    return;
                }

                // Payment still processing, continue polling
                setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
            } catch (err) {
                console.error('Error checking payment status:', err);
                if (attempts < maxAttempts - 1) {
                    setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
                } else {
                    setError('Failed to verify payment. Please check your email or contact support.');
                    setLoading(false);
                }
            }
        };

        if (sessionId) {
            // Stripe flow - poll for payment status
            pollPaymentStatus();
        } else if (legacyState?.downloadLinks?.length) {
            // Legacy PayPal flow
            setOrderData({
                downloadLinks: legacyState.downloadLinks,
                email: legacyState.email || '',
                orderId: legacyState.orderId || ''
            });
            setLoading(false);
        } else {
            // No session or state
            setLoading(false);
        }
    }, [sessionId, legacyState, clearCart]);

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-copper-500 animate-spin mx-auto mb-4" />
                    <h2 className="font-heading text-2xl text-nature-900 mb-2">Processing your order...</h2>
                    <p className="text-nature-800/60 font-body">Please wait while we confirm your payment.</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24 flex items-center justify-center">
                <div className="text-center max-w-md px-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">⚠️</span>
                    </div>
                    <h2 className="font-heading text-2xl text-nature-900 mb-4">{error}</h2>
                    <Link to="/cart">
                        <Button className="rounded-full bg-copper-500 hover:bg-copper-600">
                            Return to Cart
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    // No order found
    if (!orderData?.downloadLinks?.length) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl text-nature-900 mb-4">No order found</h2>
                    <Link to="/recordings">
                        <Button className="rounded-full bg-copper-500 hover:bg-copper-600">
                            Browse Recordings
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    const { downloadLinks, email, orderId } = orderData;

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <div className="max-w-3xl mx-auto px-6 py-12">
                <div className="text-center mb-12 opacity-0 animate-fade-in-up">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h1 className="font-heading text-4xl font-light text-nature-900 mb-4">
                        Thank <span className="italic">You!</span>
                    </h1>
                    <p className="text-nature-800/70 font-body">
                        Your purchase was successful. Your downloads are ready below.
                    </p>
                </div>

                {/* Order Info */}
                <div className="bg-white rounded-2xl p-6 shadow-soft mb-8 opacity-0 animate-fade-in-up stagger-1">
                    {email && (
                        <div className="flex items-center gap-3 mb-4">
                            <Mail className="w-5 h-5 text-copper-500" />
                            <span className="font-body text-nature-800">
                                Confirmation sent to: <strong>{email}</strong>
                            </span>
                        </div>
                    )}
                    {orderId && (
                        <p className="text-sm text-nature-800/60 font-body">
                            Order ID: {orderId}
                        </p>
                    )}
                </div>

                {/* Download Links */}
                <div className="bg-white rounded-2xl p-6 shadow-soft opacity-0 animate-fade-in-up stagger-2">
                    <h2 className="font-heading text-xl font-semibold text-nature-900 mb-6 flex items-center gap-2">
                        <Download className="w-5 h-5 text-copper-500" />
                        Your Downloads
                    </h2>
                    
                    <div className="space-y-4">
                        {downloadLinks.map((link, index) => (
                            <div 
                                key={link.recording_id || index}
                                className="flex items-center justify-between p-4 bg-nature-50 rounded-xl opacity-0 animate-fade-in-up"
                                style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                                data-testid={`download-${link.recording_id}`}
                            >
                                <div>
                                    <h3 className="font-heading font-semibold text-nature-900">
                                        {link.title}
                                    </h3>
                                    <p className="text-sm text-nature-800/60 font-body">
                                        MP3 Audio File
                                    </p>
                                </div>
                                <a
                                    href={link.url}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full border border-copper-500 text-copper-500 hover:bg-copper-500 hover:text-white transition-colors"
                                    data-testid={`download-btn-${link.recording_id}`}
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </a>
                            </div>
                        ))}
                    </div>
                    
                    <p className="mt-6 text-sm text-nature-800/60 font-body text-center">
                        Download links are valid for 7 days. Save your files to your device.
                    </p>
                </div>

                {/* Next Steps */}
                <div className="mt-8 text-center opacity-0 animate-fade-in-up stagger-3">
                    <p className="text-nature-800/70 font-body mb-6">
                        These recordings are yours to keep forever. Listen as often as you like, 
                        at your own pace, whenever you feel the need for support.
                    </p>
                    <Link to="/recordings" data-testid="browse-more-btn">
                        <Button className="rounded-full bg-copper-500 hover:bg-copper-600">
                            Browse More Recordings
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Success;
